import { useCallback, useEffect, useState } from 'react';
import { useSetAtom } from 'jotai';
import { useTranslation } from 'next-i18next';
import appConfig from 'app-config';
import { DTO_PROFILE_CARD_NICKNAME_REVIEW_STATE_TYPES as NicknameReviewStatus, DTOProfileCard } from 'src/apis/dto/DTOProfileCard';
import Button from 'src/components/Button';
import EditNicknameModal from 'src/components/EditNicknameModal';
import EditPreferredLanguageModal from 'src/components/EditPreferredLanguageModal';
import { CARD_HEIGHT, Desc, IcChevronRight, FieldIconLanguages, FieldIconNickname, ModalContent, ModalFooter, ModalLayout, NicknamePendingDescription, LabelWrap, ProfileImageWrap, Section, Subtitle, WarningIcon, TextField } from 'src/components/EditProfileModal/styles';
import Gap from 'src/components/Gap';
import { MobileModalType, ModalHeader, ModalHeaderTitle, ModalLoading } from 'src/components/ModalLayout';
import { defaultProfileImage } from 'src/components/ProfileImage';
import { GridContextProvider, GridItem } from 'src/components/react-grid-drag';
import Textarea from 'src/components/Textarea';
import useIsRtl from 'src/hooks/useIsRtl';
import { useMyProfile } from 'src/hooks/useMyProfile';
import { useUpdateProfileMutation } from 'src/hooks/useProfileQuery';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import { showToastAtom } from 'src/stores/toast/atoms';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { ModalType } from 'src/types/Modal';
import { ToastType } from 'src/types/Toast';
import { pushEvent } from 'src/utils/event-gateway';
import Add from './Add';
import HashtagEditor from './HashtagEditor';
import ProfileImageCard from './ProfileImageCard';
import { useLocalProfileImages } from './useLocalProfileImages';
const MAX_PROFILE_IMAGE_COUNT = 12;
interface EditProfileModalContentProps {
  profile: DTOProfileCard;
}
const EditProfileModalContent: React.FC<EditProfileModalContentProps> = ({
  profile
}) => {
  const {
    t
  } = useTranslation();
  const closeModal = useSetAtom(closeModalAtom);
  const showModal = useSetAtom(showModalAtom);
  const showToast = useSetAtom(showToastAtom);
  const {
    mutate: updateProfile,
    isPending: isUpdatingProfile
  } = useUpdateProfileMutation({
    onSuccess() {
      closeModal(ModalType.EDIT_PROFILE);
    }
  });
  const [profileBio, setProfileBio] = useState(profile.profileBio ?? '');
  const [profileHashtags, setProfileHashtags] = useState<string[]>(profile.hashtagInfos?.map(({
    hashtag
  }) => hashtag) ?? []);
  const {
    localImages,
    isSaving,
    add,
    remove,
    swapImages,
    changeMain,
    submit,
    cleanup
  } = useLocalProfileImages({
    initialImages: profile.profileImageInfos ?? []
  });
  const isRtl = useIsRtl();
  const handleDragEnd = useCallback((_sourceId: string, sourceIndex: number, targetIndex: number) => {
    swapImages(sourceIndex, targetIndex);
  }, [swapImages]);
  const handleChangeBio = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setProfileBio(e.target.value);
  }, []);
  const handleClickDone = async () => {
    pushEvent({
      eventType: EVENT_TYPE.EDIT_PROFILE,
      eventName: EVENT_NAME.EDIT_PROFILE_MODAL__DONE
    });
    try {
      await submit();
      updateProfile([profileBio, profileHashtags]);
    } catch {
      showToast({
        type: ToastType.ERROR,
        message: 'common__try_again'
      });
    }
  };
  const handleClickCancel = () => {
    pushEvent({
      eventType: EVENT_TYPE.EDIT_PROFILE,
      eventName: EVENT_NAME.EDIT_PROFILE_MODAL__CLICK_CLOSE
    });
    closeModal(ModalType.EDIT_PROFILE);
  };
  useEffect(() => {
    pushEvent({
      eventType: EVENT_TYPE.EDIT_PROFILE,
      eventName: EVENT_NAME.EDIT_PROFILE_MODAL__IMPRESSION
    });
    return cleanup;
  }, [cleanup]);
  const handleClickNickname = () => {
    showModal({
      key: ModalType.EDIT_NICKNAME,
      component: EditNicknameModal
    });
  };
  const handleClickLanguage = () => {
    pushEvent({
      eventName: EVENT_NAME.EDIT_PROFILE__CLICK_PREFERRED_LANGUAGE,
      eventType: EVENT_TYPE.EDIT_PROFILE,
      eventParams: {
        action_category: 'click',
        tab: 'edit_profile',
        page: 'main',
        target: 'preferred_language'
      }
    });
    showModal({
      key: ModalType.EDIT_PREFER_LANGUAGE,
      component: EditPreferredLanguageModal
    });
  };
  const languageEntries = appConfig.features.profilePreferredLanguage.enabled ? profile.userPreferredLanguageEntries : profile.userLanguageEntries;
  return <ModalLayout mobileType={MobileModalType.SCREEN_MODAL} isDim onClose={handleClickCancel} data-sentry-element="ModalLayout" data-sentry-component="EditProfileModalContent" data-sentry-source-file="index.tsx">
      <ModalHeader data-sentry-element="ModalHeader" data-sentry-source-file="index.tsx">
        <ModalHeaderTitle mobileType={MobileModalType.SCREEN_MODAL} data-sentry-element="ModalHeaderTitle" data-sentry-source-file="index.tsx">
          {t('EDIT_PROFILE_TITLE')}
        </ModalHeaderTitle>
      </ModalHeader>
      <ModalContent data-sentry-element="ModalContent" data-sentry-source-file="index.tsx">
        <Section data-sentry-element="Section" data-sentry-source-file="index.tsx">
          <GridContextProvider onChange={handleDragEnd} isRtl={isRtl} data-sentry-element="GridContextProvider" data-sentry-source-file="index.tsx">
            <ProfileImageWrap id='profile-image-wrap' boxesPerRow={3} rowHeight={CARD_HEIGHT + 9} data-sentry-element="ProfileImageWrap" data-sentry-source-file="index.tsx">
              {(localImages.length ? localImages : [{
              type: 'server' as const,
              id: 0,
              profileImageUrl: defaultProfileImage
            }]).map((image, index) => <GridItem key={image.type === 'server' ? image.id : image.tempId}>
                  <ProfileImageCard profileImageUrl={image.type === 'server' ? image.profileImageUrl : image.previewUrl} isMain={index === 0} onDelete={() => remove(image)} onMainImageChange={index === 0 ? changeMain : undefined} />
                </GridItem>)}
              {localImages.length < MAX_PROFILE_IMAGE_COUNT && <GridItem disabled>
                  <Add onImageSelected={add} />
                </GridItem>}
            </ProfileImageWrap>
          </GridContextProvider>
          <Desc data-sentry-element="Desc" data-sentry-source-file="index.tsx">{t('PROFILE_IMAGE_DESC')}</Desc>
        </Section>
        <Section data-sentry-element="Section" data-sentry-source-file="index.tsx">
          <Subtitle data-sentry-element="Subtitle" data-sentry-source-file="index.tsx">{t('PROFILE_ABOUT_ME')}</Subtitle>
          <Textarea placeholder={t('PROFILE_ABOUT_ME_PLACEHOLDER')} onChange={handleChangeBio} value={profileBio} maxLength={250} hasCount data-sentry-element="Textarea" data-sentry-source-file="index.tsx" />
        </Section>
        <Section data-sentry-element="Section" data-sentry-source-file="index.tsx">
          <Subtitle data-sentry-element="Subtitle" data-sentry-source-file="index.tsx">{t('PROFILE_HASHTAG')}</Subtitle>
          <HashtagEditor value={profileHashtags} onChange={setProfileHashtags} data-sentry-element="HashtagEditor" data-sentry-source-file="index.tsx" />
        </Section>
        <Section data-sentry-element="Section" data-sentry-source-file="index.tsx">
          <Subtitle data-sentry-element="Subtitle" data-sentry-source-file="index.tsx">{t('PROFILE_MY_INFO')}</Subtitle>
          <LabelWrap onClick={handleClickNickname} data-sentry-element="LabelWrap" data-sentry-source-file="index.tsx">
            <FieldIconNickname data-sentry-element="FieldIconNickname" data-sentry-source-file="index.tsx" />
            <Gap width={8} data-sentry-element="Gap" data-sentry-source-file="index.tsx" />
            <TextField data-sentry-element="TextField" data-sentry-source-file="index.tsx">{profile.nickname || t('PROFILE_NICKNAME_PLACEHOLDER')}</TextField>
            {profile.nicknameReviewState === NicknameReviewStatus.PENDING && <WarningIcon />}
            <Gap width={8} data-sentry-element="Gap" data-sentry-source-file="index.tsx" />
            <IcChevronRight data-sentry-element="IcChevronRight" data-sentry-source-file="index.tsx" />
          </LabelWrap>
          {profile.nicknameReviewState === NicknameReviewStatus.PENDING && <NicknamePendingDescription>{t('edit_nickname_pending')}</NicknamePendingDescription>}
          <LabelWrap onClick={appConfig.features.profilePreferredLanguage.enabled ? handleClickLanguage : undefined} disabled={!appConfig.features.profilePreferredLanguage.enabled} data-sentry-element="LabelWrap" data-sentry-source-file="index.tsx">
            <FieldIconLanguages data-sentry-element="FieldIconLanguages" data-sentry-source-file="index.tsx" />
            <Gap width={8} data-sentry-element="Gap" data-sentry-source-file="index.tsx" />
            <TextField data-sentry-element="TextField" data-sentry-source-file="index.tsx">
              {languageEntries?.map(({
              displayName
            }) => displayName).join(', ') || t('PROFILE_LANGUAGE_PLACEHOLDER')}
            </TextField>
            {appConfig.features.profilePreferredLanguage.enabled && <>
                <Gap width={8} />
                <IcChevronRight />
              </>}
          </LabelWrap>
        </Section>
      </ModalContent>
      <ModalFooter data-sentry-element="ModalFooter" data-sentry-source-file="index.tsx">
        <Button color='primary' onClick={handleClickDone} id='EDIT_PROFILE_COMPLETE' loading={isSaving || isUpdatingProfile} data-sentry-element="Button" data-sentry-source-file="index.tsx">
          {t('complete')}
        </Button>
      </ModalFooter>
    </ModalLayout>;
};
const EditProfileModal: React.FC = () => {
  const profile = useMyProfile();
  if (!profile) {
    return <ModalLoading />;
  }
  return <EditProfileModalContent profile={profile} data-sentry-element="EditProfileModalContent" data-sentry-component="EditProfileModal" data-sentry-source-file="index.tsx" />;
};
export default EditProfileModal;